/**
 * Answer persistence for the applicant application forms.
 *
 * A submission that fails after nine steps of typing used to cost the applicant every
 * answer, because the only copy lived in component state — a reload to escape a stuck
 * "Submitting..." button threw it all away. This keeps the answers in localStorage so a
 * failed submit, a reload, or a closed tab is recoverable.
 *
 * Two deliberate limits:
 *  - `File` objects cannot be serialized, so uploads are NOT saved. `pendingFilePurposes`
 *    records that files were attached so the form can say "re-attach your documents"
 *    instead of letting the applicant discover an empty file input.
 *  - The stored answers are applicant PII (names, emails, addresses), so the signature
 *    data URL is dropped, drafts expire, and a successful filing clears them.
 */

const KEY_PREFIX = 'civicode:application-draft';
const DRAFT_VERSION = 1;
export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// Never persisted: a base64 signature image is both large and the most sensitive thing
// on the form, and it is cheap for the applicant to re-draw.
const OMITTED_FIELDS = ['applicant_signature', 'applicant_signature_evidence', 'signature', 'signature_evidence', 'signature_data_url'];

export const draftKey = (scope, subject = 'anon') => `${KEY_PREFIX}:${scope}:${subject}`;

/**
 * The key every form should use. A draft belongs to one signed-in user, one program, one
 * license year and one task — a new filing (optionally pinned to the record the form was
 * opened from), or an edit of one specific application — so it never resurfaces for
 * another person, another property or another year. Returns null without a signed-in
 * user: answers typed while signed out are never persisted. `year` should be the license
 * type's fiscal year when the form knows it; the calendar year otherwise.
 */
export function applicationDraftKey({
  userId, program, mode = 'create', recordId = null, context = null, year = new Date().getFullYear(),
}) {
  if (userId == null || userId === '' || program == null || program === '') return null;
  const task = mode === 'edit' ? `edit-${recordId}` : 'create';
  return `${KEY_PREFIX}:${program}:${userId}:${year}:${task}${context ? `:${context}` : ''}`;
}

const isFile = (value) => typeof Blob !== 'undefined' && value instanceof Blob;

const stripOmitted = (value) => {
  // File bytes cannot be serialized (they would round-trip as `{}`), so drop them.
  if (Array.isArray(value)) return value.filter((item) => !isFile(item)).map(stripOmitted);
  if (!value || typeof value !== 'object') return value;
  const out = {};
  Object.entries(value).forEach(([key, inner]) => {
    if (OMITTED_FIELDS.includes(key) || isFile(inner)) return;
    out[key] = stripOmitted(inner);
  });
  return out;
};

const hasOmittedSignature = (value) => {
  if (!value || typeof value !== 'object') return false;
  return Object.entries(value).some(([key, inner]) => (
    (OMITTED_FIELDS.includes(key) && Boolean(inner)) || hasOmittedSignature(inner)
  ));
};

/**
 * Persist the answers. Every storage access is guarded — private mode throws on write.
 * `stepKey` is the stable id of the step the applicant was on (the index alone changes
 * meaning when conditional steps such as the renewal step come and go); `context` holds
 * what the form needs to make the restored answers recognizable again — the chosen
 * location's label, the renewal choice, the selected applicant.
 */
export function saveDraft(key, {
  formData, currentStepIndex, stepKey, context, clientMutationId, pendingFilePurposes,
}) {
  if (!key) return false;
  try {
    localStorage.setItem(key, JSON.stringify({
      version: DRAFT_VERSION,
      savedAt: Date.now(),
      formData: stripOmitted(formData),
      currentStepIndex: currentStepIndex ?? 0,
      stepKey: stepKey || null,
      context: stripOmitted(context || {}),
      // Persisted so a retry *after a reload* still dedupes against the first attempt.
      clientMutationId: clientMutationId || null,
      pendingFilePurposes: pendingFilePurposes || [],
      // The drawn signature is dropped above; remember that one existed so the restored
      // form can ask for it again instead of failing at Submit.
      needsSignature: hasOmittedSignature(formData),
    }));
    return true;
  } catch (e) {
    return false;
  }
}

export const LEGACY_DRAFT_STEP_MAP = {
  business: 'location',
  address: 'location',
  prior_app: 'location',
  prior: 'location',
  property: 'location',
  owner: 'applicant_contact',
  owner_is_applicant: 'applicant_contact',
  owner_group: 'applicant_contact',
  owner_is_primary_contact: 'applicant_contact',
  primary_contact_group: 'applicant_contact',
  contacts: 'applicant_contact',
  delivery: 'applicant_contact',
  renewal: 'details',
  business_details: 'details',
  fees: 'details',
  licensing: 'documents',
  lead_documents: 'documents',
  attestation: 'review',
  attestation_group: 'review',
};

/**
 * Where a restored draft should land: the saved step if it still exists, otherwise the
 * consolidated step it mapped into, or the saved index clamped into the current step list.
 */
export function resolveDraftStep(steps, { stepKey, currentStepIndex } = {}, keyOf = (step) => step.key) {
  if (!steps.length) return 0;
  const byExactKey = stepKey ? steps.findIndex((step) => keyOf(step) === stepKey) : -1;
  if (byExactKey >= 0) return byExactKey;
  const mappedKey = LEGACY_DRAFT_STEP_MAP[stepKey];
  if (mappedKey) {
    const byMappedKey = steps.findIndex((step) => keyOf(step) === mappedKey);
    if (byMappedKey >= 0) return byMappedKey;
  }
  return Math.min(Math.max(Number(currentStepIndex) || 0, 0), steps.length - 1);
}

/**
 * Read a usable draft, or null. Returns null (and clears) for a draft from an older
 * version or one past its expiry, so stale answers never resurface silently.
 */
export function loadDraft(key) {
  if (!key) return null;
  let raw;
  try {
    raw = localStorage.getItem(key);
  } catch (e) {
    return null;
  }
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    clearDraft(key);
    return null;
  }

  if (!parsed || parsed.version !== DRAFT_VERSION || !parsed.formData) {
    clearDraft(key);
    return null;
  }
  if (!parsed.savedAt || Date.now() - parsed.savedAt > DRAFT_MAX_AGE_MS) {
    clearDraft(key);
    return null;
  }
  return parsed;
}

export function clearDraft(key) {
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // Nothing to do — a draft we cannot remove is still expiry-capped on read.
  }
}

/**
 * Remove every application draft on this device. Called on a deliberate sign-out so the
 * next person at a shared computer finds nothing; an expired session does not call it,
 * so the same applicant can sign back in and resume.
 */
export function clearAllDrafts() {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(`${KEY_PREFIX}:`))
      .forEach((key) => localStorage.removeItem(key));
  } catch (e) {
    // Storage unavailable — there is nothing we could have saved either.
  }
}

/** "Saved 3 minutes ago" for the restore prompt, so the applicant can judge relevance. */
export function describeDraftAge(savedAt, now = Date.now()) {
  if (!savedAt) return 'earlier';
  const minutes = Math.round((now - savedAt) / 60000);
  if (minutes < 1) return 'moments ago';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
